import { EventEmitter, Injectable, inject } from '@angular/core';
import {FieldValue as FieldValueInterface} from '../../../interface/api/FieldValue';
import {Field as FieldInterface} from '../../../interface/api/Field';
import {TranslateService} from '@ngx-translate/core';
import {Formulaire as FormulaireInterface} from '../../../interface/api/Formulaire';
import {Field} from '../../../class/api/Field';
import {FormulaireBloc} from '../../../class/api/FormulaireBloc';
import {FormulaireEtape} from '../../../class/api/FormulaireEtape';
import {HydraService} from '../../../core/services/hydra.service';
import {FieldValue} from '../../../class/api/FieldValue';
import {FormulaireSaisie} from '../../../interface/api/FormulaireSaisie';

export class SavingRequest {
    public readonly notify: boolean = false;
    public readonly withFullValidation: boolean = false;

    constructor(notify: boolean = false, withFullValidation: boolean = false) {
        this.notify = notify === true;
        this.withFullValidation = withFullValidation === true;
    }
}


@Injectable()
export class FieldValueManagerService {
    translate = inject(TranslateService);


    private _formulaire: FormulaireInterface;
    public get formulaire(): FormulaireInterface {
        return this._formulaire;
    }

    public set formulaire(value: FormulaireInterface) {
        this._formulaire = value;
    }


    //Demande la sauvegarder "Général" Dans le cas ou on est dans le formulaire du dossier
    public runSaveDossierEvent: EventEmitter<SavingRequest> = new EventEmitter();
    private fieldArray: any[] = [];

    //Demande la sauvegarder "Formulaire Autonome" Dans le cas ou on modifie un formulaire a part
    public runSaveFormulaireSaisieAutonome: EventEmitter<SavingRequest> = new EventEmitter();
    private fieldArrayAutonome: any[] = [];

    /**
     * Identités (@id / id) des field values retirées du store mais toujours présentes en base.
     * Si le champ est réactivé avant la prochaine sauvegarde (ex : "oui" -> "non" -> "oui"),
     * on réutilise la field value existante au lieu d'en créer une seconde, ce qui provoquerait
     * la violation d'unicité "Déjà une valeur pour ce champ".
     */
    private removedFieldValueIdentities: { [fieldArrayKey: string]: any } = {
        fieldArray: {},
        fieldArrayAutonome: {}
    };

    // Toutes les fonctions concernant la sauvegarde "Généralisé" :
    public eventEtapeError: EventEmitter<any[]> = new EventEmitter();
    public etapeError: any[] = [];


    private _saveInProgress = false;
    private scheduledSaves = {
        fieldArray: false,
        fieldArrayAutonome: false
    };

    get hasRegularScheduledSaves(): boolean {
        return this.scheduledSaves.fieldArray;
    }

    get hasAutonomeScheduledSaves(): boolean {
        return this.scheduledSaves.fieldArrayAutonome;
    }

    get saveInProgress(): boolean {
        return this._saveInProgress;
    }

    set saveInProgress(bool: boolean) {
        if (!bool) {
            this.finishRequest();
        } else {
            this._saveInProgress = true;
        }
    }

    public finishRequest(launchNextScheduledRequest: boolean = true, key: string = 'fieldArray') {
        this._saveInProgress = false;

        if (!launchNextScheduledRequest) {
            this.launchOtherScheduledSaves(key);
            return;
        }

        /**
         * Note importante :
         * actuellement permet uniquement de sauvegarder les nouvelles fieldValues
         * si la mise à jour de field value entre le départ de la précédente sauvegarde et
         * le retour du serveur doit etre pris en compte, il faudra modifier setField
         */
        if (this.scheduledSaves[key] === true) {
            this.runSaveDossier(key);
            return;
        } else {
            this.launchOtherScheduledSaves(key);
        }
    }

    private launchOtherScheduledSaves(key: string) {
        const otherKey = key == 'fieldArray' ? 'fieldArrayAutonome' : 'fieldArray';
        if (this.scheduledSaves[otherKey] === true) {
            this.runSaveDossier(otherKey);
        }
    }

    public resetErrors() {
        this.etapeError = [];
        this.eventEtapeError.emit(this.etapeError);
    }

    public setEtapeError(etapeId: number, fieldId: number) {
        const key = 'ETAPE_' + etapeId;
        if (this.etapeError[key] != undefined && this.etapeError[key] != null) {
            let item = this.etapeError[key];
            if (
                item != null &&
                item != '' &&
                item.length
            ) {
                item = [...item, 'FIELD_' + fieldId];
                this.etapeError[key] = item;
            }
        } else {
            this.etapeError[key] = ['FIELD_' + fieldId];
        }
        this.eventEtapeError.emit(this.etapeError);
    }

    public getError() {
        return [...this.etapeError];
    }

    public getEtapeErrors(etapeId: number): any {
        if (this.etapeError['ETAPE_' + etapeId] != undefined) {
            return this.etapeError['ETAPE_' + etapeId];
        } else {
            return [];
        }
    }

    public etapeHasError(etapeId: number): boolean {
        const error = this.etapeError['ETAPE_' + etapeId];
        return error != undefined && error != null && error.length;
    }

    public saveAll(savingRequest: SavingRequest = null): void {
        if (savingRequest && savingRequest.withFullValidation == true) {
            throw new Error('Le système ne permet pas de sauvegarder les équipes associées puis de valider');
        }

        this.runSaveDossier('fieldArray', savingRequest);

        if (Object.keys(this.fieldArrayAutonome).length && this.runSaveFormulaireSaisieAutonome.observers.length) {
            this.runSaveDossier('fieldArrayAutonome');
        }
    }

    public runSaveDossier(key: string = 'fieldArray', savingRequest: SavingRequest = null) {
        // operation delayed
        if (this._saveInProgress) {
            this.scheduledSaves[key] = true;
            return;
        }
        this._saveInProgress = true;
        this.scheduledSaves[key] = false;

        if (!savingRequest) {
            savingRequest = new SavingRequest();
        }

        if (key == 'fieldArray') {
            this.runSaveDossierEvent.emit(savingRequest);
        } else {
            this.runSaveFormulaireSaisieAutonome.emit(savingRequest);
        }
    }

    public resetAll() {
        this.fieldArray = [];
        this.etapeError = [];
        this.fieldArrayAutonome = [];
        this.removedFieldValueIdentities = {
            fieldArray: {},
            fieldArrayAutonome: {}
        };
        this.formulaire = null;
        this._formulaire = null;
        this._saveInProgress = false;
        this.scheduledSaves.fieldArray = false;
        this.scheduledSaves.fieldArrayAutonome = false;
    }

    public resetService(fieldArrayKey: string = 'fieldArray') {
        if (fieldArrayKey === 'fieldArray') {
            this.fieldArray = [];
            this.etapeError = [];
        } else {
            this.fieldArrayAutonome = [];
        }
        this.removedFieldValueIdentities[fieldArrayKey] = {};
    }

    /**
     * @deprecated utiliser plutôt setValueForField() ou setFieldValue()
     * @param fieldId
     * @param fieldValue
     * @param fieldArrayKey
     * @param updateNotification
     */
    public setField(fieldId: number,
                    fieldValue: any,
                    fieldArrayKey: string = 'fieldArray',
                    updateNotification = true) {
        // On regarde si on a pas déjà setter ce field
        if (fieldValue && fieldValue.value && !isNaN(fieldValue.value)) {
            fieldValue['value'] = '' + fieldValue.value;
        }
        if (fieldId != undefined && +fieldId) {
            this.restoreRemovedIdentity(+fieldId, fieldValue, fieldArrayKey);
            this[fieldArrayKey]['_' + fieldId] = fieldValue;
        }

        if (updateNotification) {
            this.updateNotification = true;
        }
    }

    public updateNotification = false;

    public setFieldValue(fieldValue: FieldValueInterface, fieldArrayKey: string = 'fieldArray', updateMessage = true): boolean {
        const field = fieldValue.field;
        if (!field) {
            return false;
        }

        const fieldId: number = this.getFieldId(field);

        if (!fieldId) {
            return false;
        }

        this.setField(
            fieldId,
            fieldValue,
            fieldArrayKey,
            updateMessage
        );
        return true;
    }

    public setValueForField(
        value: string,
        field: string | FieldInterface,
        fieldArrayKey: string = 'fieldArray',
        updateMessage = true
    ) {
        const fieldId = this.getFieldId(field);

        if (!fieldId) {
            throw new Error(
                'Field non présent dans le formulaire'
            );
        }

        let fieldValue = this.getFieldValue(
            fieldId,
            fieldArrayKey
        );

        if (fieldValue == null) {
            fieldValue = new FieldValue();
            delete fieldValue.formulaireSaisie;
            fieldValue.field = HydraService.extractIRI(field);
        }

        fieldValue.value = value;

        this.restoreRemovedIdentity(fieldId, fieldValue, fieldArrayKey);

        this[fieldArrayKey]['_' + fieldId] = fieldValue;

        if (updateMessage) {
            this.updateNotification = true;
        }
    }


    public removeFieldValue(fieldId: number, fieldArrayKey: string = 'fieldArray') {
        const key = '_' + fieldId;
        const fieldValue = this[fieldArrayKey][key];

        // La suppression n'est que locale : tant que la sauvegarde n'a pas eu lieu, la valeur
        // existe toujours en base, on conserve donc son identité pour pouvoir la réutiliser.
        if (fieldValue && fieldValue['@id'] && this.removedFieldValueIdentities[fieldArrayKey]) {
            this.removedFieldValueIdentities[fieldArrayKey][key] = {
                '@id': fieldValue['@id'],
                id: fieldValue['id']
            };
        }

        delete this[fieldArrayKey][key];
    }

    /**
     * Réattache à une field value nouvellement créée l'identité de celle qui avait été retirée
     * du store pour le même field et qui n'a pas encore été supprimée en base.
     */
    private restoreRemovedIdentity(fieldId: number, fieldValue: any, fieldArrayKey: string = 'fieldArray') {
        if (!fieldValue || fieldValue['@id']) {
            return;
        }

        const identities = this.removedFieldValueIdentities[fieldArrayKey];
        if (!identities) {
            return;
        }

        const key = '_' + fieldId;
        if (!identities[key]) {
            return;
        }

        fieldValue['@id'] = identities[key]['@id'];
        fieldValue['id'] = identities[key]['id'];
        delete identities[key];
    }

    /**
     * @deprecated fonction mal nommée, merci d'utiliser removeFieldValue() à la place
     * @param fieldId
     * @param fieldArrayKey
     */
    public removeField(fieldId: number, fieldArrayKey: string = 'fieldArray') {
        this.removeFieldValue(fieldId, fieldArrayKey);
    }

    public getFieldValue(field: number | FieldInterface | string, fieldArrayKey: string = 'fieldArray'): FieldValueInterface | null {
        const fieldId = this.getFieldId(field);

        if (this[fieldArrayKey]['_' + fieldId] != undefined) {
            return this[fieldArrayKey]['_' + fieldId] as FieldValueInterface;
        } else {
            return null;
        }
    }

    /**
     * @deprecated fonction mal nommée, merci d'utiliser getFieldValue() à la place
     * @param fieldId
     * @param fieldArrayKey
     */
    public getField(fieldId: number, fieldArrayKey: string = 'fieldArray'): FieldValueInterface | null {
        return this.getFieldValue(fieldId, fieldArrayKey);
    }

    public getFields(fieldArrayKey: string = 'fieldArray'): any[] {
        const temp = [];
        for (const item in this[fieldArrayKey]) {
            temp.push(this[fieldArrayKey][item]);
        }
        return temp;
    }

    // Autres
    public testIfFieldValueExistAndNotEmpty(fieldValue: FieldValueInterface): boolean {
        if (fieldValue == null || !fieldValue) {
            return false;
        }

        return !(fieldValue['value'] == undefined ||
            fieldValue['value'] == null ||
            fieldValue['value'] == '');
    }

    getNbrEquipeTotal(): number {
        let equipes = 1;
        let fieldId = null;
        [...this._formulaire.formulaireEtapes].map((etape: FormulaireEtape) => {
            [...etape.formulaireBlocs].map((bloc: FormulaireBloc) => {
                [...bloc.fields].map((field: Field) => {
                    if (field.fieldType == '/api/field_types/equipeAssociee') {
                        fieldId = field.id;
                    }
                });
            });
            return etape;
        });

        if (fieldId !== null && this.getField(fieldId) !== null && this.getField(fieldId)['resolvedValue'] !== null) {
            equipes = equipes + this.getField(fieldId)['resolvedValue'].length;
        }
        return equipes;
    }


    /**
     * @return number|null
     * @param field FieldInterface | string
     */
    private getFieldId(field: FieldInterface | string | number): number | null {

        if(typeof field === 'number'){
            return field;
        }

        if (typeof field === 'string') {
            return this.getFieldIdFromIri(field);
        }

        let fieldId = field.id;
        if (!fieldId) {
            const fieldIRI = HydraService.extractIRI(field);
            if (!fieldIRI) {
                return null;
            }
            fieldId = this.getFieldIdFromIri(fieldIRI);
        }

        return fieldId;
    }

    /**
     * @return number|null
     * @param field string
     */
    public getFieldIdFromIri(field: string): number | null {
        const split = field.split('/');
        const fieldId = parseInt(split[3]);
        if (isNaN(fieldId)) {
            return null;
        }
        return fieldId;
    }

    /**
     * On n'écrase pas les valeurs, car d'autres données sont saisie le temps de la sauvegarde
     * Par contre on complète les Iris, et on ajoute d'éventuelles nouvelles données
     */
    mergeValues(formulaireSaisie: FormulaireSaisie, fieldArrayKey: string = 'fieldArray') {
        const fieldValues = (formulaireSaisie.fieldValues ?? []) as FieldValueInterface[];

        fieldValues.forEach(
            (item: FieldValueInterface) => {
                const actualFieldValue = this.getFieldValue(
                    item.field as FieldInterface,
                    fieldArrayKey
                );

                if(actualFieldValue){
                    actualFieldValue['@id'] = item['@id'];
                }else{
                    console.log('mergeValues : Nouvelle valeur reçue du serveur ' + item['@id']);
                    this.setFieldValue(item, fieldArrayKey);
                }
            }
        );

        this.forgetRemovedIdentitiesAbsentFrom(fieldValues, fieldArrayKey);
    }

    /**
     * Après une sauvegarde réussie, les field values retirées du store ont été supprimées en base :
     * leur identité ne doit plus être réutilisée. On ne purge que celles réellement absentes de la
     * réponse serveur, une suppression ayant pu intervenir pendant qu'une sauvegarde était en cours.
     */
    private forgetRemovedIdentitiesAbsentFrom(fieldValues: FieldValueInterface[], fieldArrayKey: string) {
        const identities = this.removedFieldValueIdentities[fieldArrayKey];
        if (!identities) {
            return;
        }

        const iris = fieldValues.map((item: FieldValueInterface) => item['@id']);

        Object.keys(identities).forEach((key: string) => {
            if (iris.indexOf(identities[key]['@id']) === -1) {
                delete identities[key];
            }
        });
    }
}
